import { supabase } from "@/integrations/supabase/client";

export type ReminderPeriod = "morning" | "afternoon" | "evening";

export type NotificationPreferences = {
  push_enabled?: boolean;
  daily_direction?: boolean;
  practice_reminders?: boolean;
  moment_updates?: boolean;
  reengagement_reminders?: boolean;
  product_news?: boolean;
  reminder_period?: ReminderPeriod;
  timezone?: string;
  push_prompt_count?: number;
  push_prompt_dismissed_at?: string;
};

export type PushCapability =
  "unsupported" | "ios-install-required" | "prompt" | "denied" | "enabled";

let pushPromptDismissedThisPage = false;

function isIosDevice() {
  return /iPad|iPhone|iPod/u.test(navigator.userAgent);
}

function isStandalone() {
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches || iosNavigator.standalone === true
  );
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
  } catch {
    return "America/Sao_Paulo";
  }
}

export function getPushCapability(): PushCapability {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  if (isIosDevice() && !isStandalone()) return "ios-install-required";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "granted") return "enabled";
  return "prompt";
}

export function mergeNotificationPreferences(
  current: NotificationPreferences,
  patch: Partial<NotificationPreferences> = {},
): NotificationPreferences {
  return {
    push_enabled: false,
    daily_direction: true,
    practice_reminders: true,
    moment_updates: true,
    reengagement_reminders: true,
    product_news: false,
    reminder_period: "morning",
    timezone: getBrowserTimezone(),
    push_prompt_count: 0,
    ...current,
    ...patch,
  };
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function isInitialPushPromptDue(preferences: NotificationPreferences) {
  const prefs = mergeNotificationPreferences(preferences);
  return !prefs.push_enabled && !pushPromptDismissedThisPage;
}

export function isContextualPushPromptDue(preferences: NotificationPreferences, now = Date.now()) {
  const prefs = mergeNotificationPreferences(preferences);
  const promptCount = prefs.push_prompt_count ?? 0;
  if (!pushPromptDismissedThisPage) return false;
  if (prefs.push_enabled || promptCount < 1 || promptCount >= 3) return false;

  const dismissedAt = Date.parse(prefs.push_prompt_dismissed_at ?? "");
  if (!Number.isFinite(dismissedAt)) return true;

  const cooldown = promptCount === 1 ? 3 * DAY_IN_MS : 14 * DAY_IN_MS;
  return now - dismissedAt >= cooldown;
}

export function recordPushPromptDismissal(
  preferences: NotificationPreferences,
  dismissedAt = new Date(),
) {
  pushPromptDismissedThisPage = true;
  const prefs = mergeNotificationPreferences(preferences);
  return mergeNotificationPreferences(prefs, {
    push_prompt_count: Math.min((prefs.push_prompt_count ?? 0) + 1, 3),
    push_prompt_dismissed_at: dismissedAt.toISOString(),
  });
}

function urlBase64ToArrayBuffer(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replaceAll("-", "+").replaceAll("_", "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes.buffer;
}

export async function registerPushServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js", {
    scope: "/",
    updateViaCache: "none",
  });
}

async function currentSubscription() {
  const registration = await registerPushServiceWorker();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

async function saveSubscription(subscription: PushSubscription) {
  const serialized = subscription.toJSON();
  const { data, error } = await supabase.functions.invoke("push-subscription", {
    method: "POST",
    body: {
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: serialized.keys,
      timezone: getBrowserTimezone(),
    },
  });
  if (error) throw error;
  if (!data?.enabled) throw new Error("Não foi possível confirmar a ativação dos lembretes.");
}

export async function enablePushNotifications() {
  const capability = getPushCapability();
  if (capability === "unsupported") {
    throw new Error("Este navegador não oferece lembretes por push.");
  }
  if (capability === "ios-install-required") {
    throw new Error("Adicione o aplicativo à Tela de Início do iPhone antes de ativar.");
  }
  if (capability === "denied") {
    throw new Error("As notificações estão bloqueadas nas configurações do navegador.");
  }

  const permission =
    Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("A permissão para lembretes não foi concedida.");

  const registration = await registerPushServiceWorker();
  if (!registration) throw new Error("Não foi possível preparar os lembretes neste aparelho.");

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const { data, error } = await supabase.functions.invoke("push-subscription", {
      method: "GET",
    });
    if (error) throw error;
    if (typeof data?.publicKey !== "string") {
      throw new Error("Os lembretes ainda estão sendo preparados. Tente novamente em instantes.");
    }
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(data.publicKey),
    });
  }

  await saveSubscription(subscription);
  return subscription;
}

export async function disablePushNotifications() {
  const subscription = await currentSubscription();
  if (!subscription) return;

  const { error } = await supabase.functions.invoke("push-subscription", {
    method: "DELETE",
    body: { endpoint: subscription.endpoint },
  });
  await subscription.unsubscribe();
  if (error) throw error;
}

export async function syncPushSubscription() {
  if (getPushCapability() !== "enabled") return false;
  const subscription = await currentSubscription();
  if (!subscription) return false;
  await saveSubscription(subscription);
  return true;
}
